"use client";

import { useState } from "react";
import { Bug, CheckCircle2, Lightbulb } from "lucide-react";
import { submitFeedback, type FeedbackType } from "@/lib/api/api-client";

interface FeedbackPanelProps {
  defaultEmail: string | null;
  pathname: string | null;
  appVersion: string | null;
}

const MIN_MESSAGE = 10;
const MAX_MESSAGE = 1000;

/**
 * Ce panneau ne propose que les deux types spontanés : `session` existe côté
 * API mais désigne une provenance — un retour que le produit est allé chercher
 * en fin d'étape — et l'offrir ici en ferait un thème à cocher, ce qu'il n'est
 * pas. D'où le sous-ensemble explicite plutôt que `Record<FeedbackType, …>`.
 */
type SpontaneousFeedbackType = Extract<FeedbackType, "idea" | "bug">;

const PLACEHOLDER: Record<SpontaneousFeedbackType, string> = {
  idea: "Ce qui te ferait gagner du temps, une fonctionnalité qui te manque…",
  bug: "Ce qui s'est passé, et ce que tu attendais à la place.",
};

const TYPE_OPTIONS: { value: SpontaneousFeedbackType; label: string; Icon: typeof Lightbulb }[] = [
  { value: "idea", label: "Une idée", Icon: Lightbulb },
  { value: "bug", label: "Un souci", Icon: Bug },
];

export function FeedbackPanel({ defaultEmail, pathname, appVersion }: FeedbackPanelProps) {
  const [type, setType] = useState<SpontaneousFeedbackType>("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const tooShort = message.trim().length < MIN_MESSAGE;

  const handleSubmit = async () => {
    if (tooShort || status === "sending") {
      return;
    }

    setStatus("sending");
    setError(null);

    try {
      await submitFeedback({
        type,
        message: message.trim(),
        email: email.trim() === "" ? null : email.trim(),
        pathname,
        appVersion,
      });
      setStatus("sent");
    } catch {
      setStatus("idle");
      setError("L'envoi a échoué. Réessaie dans un instant.");
    }
  };

  const handleReset = () => {
    setType("idea");
    setMessage("");
    setEmail(defaultEmail ?? "");
    setStatus("idle");
    setError(null);
  };

  if (status === "sent") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-3 px-6 py-12 text-center"
      >
        <span
          className="grid place-items-center w-12 h-12 rounded-full"
          style={{ backgroundColor: "rgba(34,197,94,0.12)" }}
        >
          <CheckCircle2 size={24} style={{ color: "var(--color-ok)" }} />
        </span>
        <h4 className="text-[15px] font-semibold text-text-primary">
          Merci, c&apos;est bien reçu 🙏
        </h4>
        <p className="text-[12px] text-text-secondary">
          On lit tous les retours, vraiment.
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="mt-1 h-9 px-4 rounded-lg text-[13px] font-medium text-text-primary cursor-pointer transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ border: "1px solid var(--color-border-subtle)" }}
        >
          Envoyer un autre retour
        </button>
      </div>
    );
  }

  const sending = status === "sending";

  return (
    <div className="flex flex-col gap-5 px-6 py-6 overflow-y-auto">
      {/* Type — segmented radiogroup */}
      <div role="radiogroup" aria-label="Type de retour" className="flex gap-2">
        {TYPE_OPTIONS.map(({ value, label, Icon }) => {
          const active = type === value;

          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={sending}
              onClick={() => setType(value)}
              className="flex flex-1 items-center justify-center gap-2 h-10 rounded-lg text-[13px] font-medium transition-colors duration-150 cursor-pointer disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              style={{
                color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                backgroundColor: active ? "var(--color-accent-subtle)" : "rgba(255,255,255,0.03)",
                border: active
                  ? "1px solid var(--color-accent)"
                  : "1px solid var(--color-border-subtle)",
              }}
            >
              <Icon size={15} strokeWidth={1.8} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Message */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="feedback-message"
          className="text-[11px] font-medium uppercase tracking-wider text-text-muted"
        >
          Dis-nous tout
        </label>
        <textarea
          id="feedback-message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
          placeholder={PLACEHOLDER[type]}
          disabled={sending}
          rows={4}
          className="resize-y rounded-lg px-3 py-2.5 text-[13px] leading-relaxed text-text-primary placeholder:text-text-muted/50 outline-none transition-colors duration-150 disabled:opacity-60"
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid var(--color-border-subtle)",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border-subtle)";
          }}
        />
        <div className="flex items-center justify-between">
          <span className="text-[11px]" style={{ color: "var(--color-err)" }}>
            {!tooShort || message.length === 0
              ? " "
              : "Ajoute quelques mots de plus pour qu'on puisse t'aider."}
          </span>
          <span className="text-[11px] text-text-muted">
            {message.length}/{MAX_MESSAGE}
          </span>
        </div>
      </div>

      {/* Email (optional) */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="feedback-email"
          className="text-[11px] font-medium uppercase tracking-wider text-text-muted"
        >
          Ton email (si tu veux qu&apos;on te réponde)
        </label>
        <input
          id="feedback-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ton@email.com"
          disabled={sending}
          className="h-10 rounded-lg px-3 text-[13px] text-text-primary placeholder:text-text-muted/50 outline-none transition-colors duration-150 disabled:opacity-60"
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid var(--color-border-subtle)",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border-subtle)";
          }}
        />
      </div>

      {error && (
        <p className="text-[12px]" style={{ color: "var(--color-err)" }}>
          {error}
        </p>
      )}

      {/* Submit */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={tooShort || sending}
          className="h-10 rounded-lg text-[13px] font-semibold text-white transition-all duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{
            backgroundColor: "var(--color-accent)",
            boxShadow: tooShort || sending ? "none" : "0 0 16px rgba(139,92,246,0.25)",
          }}
        >
          {sending ? "Envoi…" : "Envoyer"}
        </button>
        <p className="text-[11px] text-text-muted text-center">
          La page actuelle et la version sont jointes automatiquement.
        </p>
      </div>
    </div>
  );
}
